import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from "@mui/material";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAnnouncer } from "../../hooks/useAnnouncer";
import { useSchedulerStore } from "../../state/scheduler.store";
import { focusRingStyles } from "../../utils/accessibility";

interface GlobalErrorDialogProps {
  open: boolean;
  onClose: () => void;
  errorMessage?: string;
  errorDescription?: string;
  errorType?: string;
  themeVariant: "light" | "dark";
}

const GlobalErrorDialog: React.FC<GlobalErrorDialogProps> = ({
  open,
  onClose,
  errorMessage,
  errorDescription,
  errorType,
  themeVariant
}) => {
  const { ui } = useSchedulerStore();
  const { t } = useTranslation();
  const { announce } = useAnnouncer();

  // Announce error to screen readers when dialog opens
  useEffect(() => {
    if (open && errorMessage) {
      announce(
        `Error: ${errorMessage}. ${errorDescription || ""}`,
        "assertive"
      );
    }
  }, [open, errorMessage, errorDescription, announce]);

  const handlePrimaryButtonClick = () => {
    if (ui?.error?.primaryButtonAction) {
      ui.error.primaryButtonAction();
    } else {
      window.location.reload();
    }
  };

  // Compute the dialog title for accessibility
  const dialogTitle =
    errorMessage ||
    t("ROOSTER.PUBLIC-APP.ERROR", {
      defaultValue: "Error"
    });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      aria-label={dialogTitle}
      role="alertdialog"
      sx={{
        backdropFilter: "blur(20px)",
        "& .MuiPaper-root": {
          background: themeVariant === "dark" ? "#121212" : "white",
          borderRadius: "5px",
          border:
            themeVariant === "dark" ? "1px solid #262626" : "1px solid white"
        }
      }}
      fullWidth
    >
      <DialogTitle id="alert-dialog-title">{dialogTitle}</DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          {errorDescription}
        </DialogContentText>
      </DialogContent>
      {errorType !== "persistent" && (
        <DialogActions>
          <Button
            onClick={handlePrimaryButtonClick}
            variant="text"
            autoFocus
            aria-label={
              ui?.error?.primaryButtonText ??
              t("ROOSTER.COMMON.OK", { defaultValue: "OK" })
            }
            sx={focusRingStyles}
          >
            {ui?.error?.primaryButtonText ??
              t("ROOSTER.COMMON.OK", {
                defaultValue: "OK"
              })}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default GlobalErrorDialog;
